import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import HabitCheckin from "@/components/HabitCheckin";
import BrotherhoodWall from "@/components/BrotherhoodWall";
import WeeklyScore from "@/components/WeeklyScore";
import SundayWellnessBanner from "@/components/SundayWellnessBanner";
import SundayCheckinBanner from "@/components/SundayCheckinBanner";
import InstallAppBanner from "@/components/InstallAppBanner";
import OnboardingProgressRing from "@/components/OnboardingProgressRing";
import { Barbell, ChartLine, Notebook, ArrowRight, Flame, Warning } from "@phosphor-icons/react";

export default function ClientToday() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [programData, setProgramData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState([]);
  const [onbStatus, setOnbStatus] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [pd, l, pr, st] = await Promise.all([
          http.get("/client/program"),
          http.get("/client/workout-logs"),
          http.get("/client/progress"),
          http.get("/client/onboarding/status"),
        ]);
        setProgramData(pd.data);
        setLogs(l.data);
        setProgress(pr.data);
        setOnbStatus(st.data);
      } catch (e) { setErr(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, [navigate]);

  const latestWeight = progress.length ? progress[progress.length - 1].weight_kg : null;
  const program = programData?.program;
  const coachNotes = programData?.coach_notes || "";
  const firstDay = program?.days?.[0];

  // Body-measurements skipped reminder: shown if no body stats AND it's been >24h since onboarded_at
  const onboardedAt = onbStatus?.onboarded_at ? new Date(onbStatus.onboarded_at) : null;
  const hoursSinceOnboard = onboardedAt ? (Date.now() - onboardedAt.getTime()) / (1000 * 60 * 60) : 0;
  const showBodyReminder = onboardedAt && progress.length === 0 && hoursSinceOnboard > 24;

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <div className="id-label mb-3">— Today —</div>
      <h1 className="id-h1 mb-2">
        What&apos;s up, <span className="text-[#FF3333]">{user?.name?.split(" ")[0] || "lifter"}</span>?
      </h1>
      <p className="text-zinc-400 font-manrope mb-10">Stay locked in. Here&apos;s your dashboard.</p>

      {user?.founder_number ? (
        <div
          className="mb-8 border border-[#FF3333] bg-[#FF3333]/5 px-5 py-4 flex items-center gap-4"
          data-testid="founder-badge"
        >
          <div className="flex flex-col items-center justify-center bg-[#FF3333] text-white px-4 py-2 leading-none shrink-0">
            <div className="font-manrope text-[9px] uppercase tracking-[0.2em] font-bold opacity-90">Founder</div>
            <div className="font-oswald text-2xl font-bold mt-0.5">#{user.founder_number}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-oswald uppercase tracking-wide text-white text-base">
              You&apos;re a Lazy Lean Founder Member.
            </div>
            <p className="text-zinc-400 text-sm font-manrope mt-0.5">
              One of the first 20 men ever to go through this program. Your number is locked to you forever.
            </p>
          </div>
        </div>
      ) : null}

      <InstallAppBanner />

      <OnboardingProgressRing status={onbStatus} />

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-6">{err}</div>}
      {loading && <div className="id-label id-pulse">Loading...</div>}

      {showBodyReminder && (
        <div className="border-l-2 border-amber-500 bg-amber-500/10 px-4 py-3 mb-6 flex items-start gap-3" data-testid="body-stats-reminder">
          <Warning size={20} weight="bold" className="text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-oswald uppercase tracking-wide text-amber-400 text-sm">Starting measurements still missing</div>
            <p className="text-zinc-300 text-sm font-manrope mt-1">
              You skipped them during onboarding. Add them now so your progress charts have a starting point.
            </p>
          </div>
          <Link to="/app/stats" className="id-btn-primary text-xs shrink-0" data-testid="body-stats-reminder-cta">
            Add now <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      )}

      <div className="mb-6">
        <SundayCheckinBanner />
        <SundayWellnessBanner />
        <WeeklyScore />
      </div>

      <HabitCheckin />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6" data-testid="client-stats">
        <Stat icon={Flame} label="Sessions" value={logs.length} testid="stat-sessions" />
        <Stat icon={ChartLine} label="Latest Weight" value={latestWeight ? `${latestWeight} kg` : "—"} testid="stat-weight" />
        <Stat icon={Notebook} label="Progress Logs" value={progress.length} testid="stat-progress" />
      </div>

      {coachNotes && (
        <div className="id-card mb-6" data-testid="today-coach-notes">
          <div className="id-label mb-3 text-[#FF3333]">★ Habits & Nutrition · From your coach</div>
          <pre className="text-zinc-200 text-sm font-manrope whitespace-pre-wrap leading-relaxed">{coachNotes}</pre>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="id-card lg:col-span-2">
          <div className="id-label mb-3">Workout Program</div>
          {program ? (
            <div data-testid="active-program-card">
              <div className="font-oswald text-3xl uppercase tracking-wide">Your Weekly Training</div>
              <div className="text-xs text-zinc-500 mt-1 font-manrope">{program.weeks} weeks · {program.days.length} workout day{program.days.length !== 1 ? "s" : ""} this week</div>
              <p className="text-zinc-400 text-sm mt-3 font-manrope">
                Coach unlocks each session as you tick the last one off. Log every set — that&apos;s how progress compounds.
              </p>

              {firstDay && (
                <div className="border-t border-[#262626] mt-5 pt-5">
                  <div className="id-label mb-2">Next Up · {firstDay.day_name}</div>
                  <ul className="space-y-1 text-sm text-zinc-300 font-manrope">
                    {firstDay.exercises.slice(0, 4).map((ex, i) => (
                      <li key={i} className="flex justify-between border-b border-[#1A1A1A] py-1">
                        <span>{ex.name}</span>
                        <span className="text-zinc-500">{ex.sets}×{ex.reps}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-5 flex gap-3">
                <Link to="/app/program" className="id-btn-primary" data-testid="open-program-button">
                  <Barbell size={16} weight="bold" /> Open Workouts
                </Link>
                <Link to="/app/progress" className="id-btn-secondary" data-testid="log-progress-button">Log Progress</Link>
              </div>
            </div>
          ) : (
            <div className="text-zinc-400 font-manrope text-sm" data-testid="no-program-message">
              Your coach is putting your plan together. While you wait — start logging your body weight.
            </div>
          )}
        </div>

        <Link to="/app/messages" className="id-card hover:border-[#FF3333] transition-colors duration-150 flex flex-col justify-between" data-testid="message-coach-card">
          <div>
            <div className="id-label mb-3">Coach Chat</div>
            <div className="font-oswald text-2xl uppercase tracking-wide">Message your coach</div>
            <p className="text-zinc-500 text-sm mt-2 font-manrope">Ping Jimmy any time — he usually replies same-day.</p>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 text-[#FF3333] text-xs uppercase tracking-[0.2em] font-bold">
            Open thread <ArrowRight size={14} weight="bold" />
          </div>
        </Link>
      </div>

      <div className="mt-6">
        <BrotherhoodWall />
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div className="id-card" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="id-label">{label}</div>
        <Icon size={20} weight="bold" className="text-[#FF3333]" />
      </div>
      <div className="id-stat">{value}</div>
    </div>
  );
}
